import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { adminApi } from "../../services/api"
import AdminIdeas from "./AdminIdeas"
import AdminDocumentations from "./AdminDocumentations"

const TABS = {
  overview: "Dashboard",
  schools: "Schools",
  courses: "Courses",
  payments: "Payments",
  referrals: "Referrals",
  students: "Students",
  ideas: "Ideas",
  documentation: "Documentation"
}
const EMPTY_COURSE_TEMPLATE = { name: "", appliesTo: "ANY", prompt: "" }

// Chapter outline is parsed from admin template text on the server.

export default function AdminDashboard() {
  const location = useLocation()
  const pathnameNorm = (location.pathname || "/").replace(/\/+$/, "") || "/"
  const isSchoolsRoute = pathnameNorm === "/admin/schools"
  const queryTab = new URLSearchParams(location.search).get("tab")
  const allowedTabs = Object.keys(TABS)
  const resolvedQueryTab = queryTab && allowedTabs.includes(queryTab) ? queryTab : null
  const isAdminDashboardOverview =
    pathnameNorm === "/admin/dashboard" &&
    (!resolvedQueryTab || resolvedQueryTab === "overview")
  const initialTab = isSchoolsRoute ? "schools" : (resolvedQueryTab || "overview")
  const [tab, setTab] = useState(initialTab)
  /** URL can be /admin/dashboard with no ?tab= while `tab` is stale — hide summary unless tab matches overview. */
  const showSummaryAnalytics = tab === "overview" && isAdminDashboardOverview
  const [schools, setSchools] = useState([])
  const [courses, setCourses] = useState([])
  const [payments, setPayments] = useState([])
  const [referrals, setReferrals] = useState([])
  const [students, setStudents] = useState([])
  const [filterStudents, setFilterStudents] = useState([])
  const [studentModalOpen, setStudentModalOpen] = useState(false)
  const [studentDetailsLoading, setStudentDetailsLoading] = useState(false)
  const [studentDetails, setStudentDetails] = useState(null)
  const [studentStatusSaving, setStudentStatusSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [schoolModalOpen, setSchoolModalOpen] = useState(false)
  const [editingSchool, setEditingSchool] = useState(null)
  const [schoolForm, setSchoolForm] = useState({ name: "", code: "" })
  const [courseModalOpen, setCourseModalOpen] = useState(false)
  const [editingCourse, setEditingCourse] = useState(null)
  const [courseForm, setCourseForm] = useState({ name: "", code: "", templatePrompt: "", promptTemplates: [{ ...EMPTY_COURSE_TEMPLATE }] })
  const [courseSeeding, setCourseSeeding] = useState(false)
  const [schoolSeeding, setSchoolSeeding] = useState(false)
  const [courseDetailsModalOpen, setCourseDetailsModalOpen] = useState(false)
  const [courseDetailsLoading, setCourseDetailsLoading] = useState(false)
  const [courseDetails, setCourseDetails] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summary, setSummary] = useState({
    students: 0,
    schools: 0,
    courses: 0,
    ideas: 0,
    docsDocumentation: 0,
    docsProposal: 0,
    docsSiwes: 0,
    referrals: 0
  })
  const navigate = useNavigate()

  const loadSummary = async () => {
    setSummaryLoading(true)
    try {
      const [
        studentsRes,
        schoolsRes,
        coursesRes,
        ideasRes,
        docsDocRes,
        docsProposalRes,
        docsSiwesRes,
        referralsRes
      ] = await Promise.all([
        adminApi.students.list({ limit: 500 }),
        adminApi.schools.list(),
        adminApi.courses.list(),
        adminApi.ideas.list({ limit: 1, page: 1 }),
        adminApi.documentations.list({ limit: 1, page: 1, generationType: 'DOCUMENTATION' }),
        adminApi.documentations.list({ limit: 1, page: 1, generationType: 'PROPOSAL' }),
        adminApi.documentations.list({ limit: 1, page: 1, generationType: 'SIWES_REPORT' }),
        adminApi.referrals.list({ limit: 500 })
      ])

      const studentsList = studentsRes?.data?.data || []
      const schoolsList = schoolsRes?.data?.data || []
      const coursesList = coursesRes?.data?.data || []
      const ideasPagination = ideasRes?.data?.data?.pagination
      const docsDocPagination = docsDocRes?.data?.data?.pagination
      const docsProposalPagination = docsProposalRes?.data?.data?.pagination
      const docsSiwesPagination = docsSiwesRes?.data?.data?.pagination
      const referralsList = referralsRes?.data?.data || []

      setSummary({
        students: studentsList.length,
        schools: schoolsList.length,
        courses: coursesList.length,
        ideas: Number(ideasPagination?.total || 0),
        docsDocumentation: Number(docsDocPagination?.total || 0),
        docsProposal: Number(docsProposalPagination?.total || 0),
        docsSiwes: Number(docsSiwesPagination?.total || 0),
        referrals: referralsList.length
      })
    } catch {
      setSummary({
        students: 0,
        schools: 0,
        courses: 0,
        ideas: 0,
        docsDocumentation: 0,
        docsProposal: 0,
        docsSiwes: 0,
        referrals: 0
      })
    } finally {
      setSummaryLoading(false)
    }
  }

  const load = async () => {
    setLoading(true)
    setError("")
    try {
      if (tab === "schools") {
        const res = await adminApi.schools.list()
        setSchools(res?.data?.data || [])
      } else if (tab === "courses") {
        const [coursesRes, schoolsRes] = await Promise.all([adminApi.courses.list(), adminApi.schools.list()])
        setCourses(coursesRes?.data?.data || [])
        setSchools(schoolsRes?.data?.data || [])
      } else if (tab === "payments") {
        const res = await adminApi.payments.list({ limit: 50, paymentStatus: "successful" })
        setPayments(res?.data?.data || [])
      } else if (tab === "referrals") {
        const res = await adminApi.referrals.list({ limit: 50, status: "unpaid" })
        setReferrals(res?.data?.data || [])
      } else {
        const res = await adminApi.students.list({ limit: 50 })
        setStudents(res?.data?.data || [])
      }
    } catch (e) {
      if (e?.response?.status === 401) return
      setError(e?.response?.data?.message || "Failed to load")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // Used for filters in the Ideas & Documentation tabs.
    adminApi.students.list({ limit: 200 })
      .then((res) => setFilterStudents(res?.data?.data || []))
      .catch(() => setFilterStudents([]))
  }, [])

  useEffect(() => {
    if (!showSummaryAnalytics) return
    loadSummary()
  }, [showSummaryAnalytics])

  useEffect(() => {
    if (tab === "overview" || tab === "ideas" || tab === "documentation") return
    load()
  }, [tab])

  // Keep selected tab in sync when navigation uses ?tab=...
  useEffect(() => {
    const t = new URLSearchParams(location.search).get("tab")
    const nextTab = isSchoolsRoute ? "schools" : (t && allowedTabs.includes(t) ? t : "overview")
    setTab(prev => (prev === nextTab ? prev : nextTab))
  }, [location.pathname, location.search, isSchoolsRoute])

  const openCreateSchool = () => {
    setEditingSchool(null)
    setError("")
    setSchoolForm({ name: "", code: "" })
    setSchoolModalOpen(true)
  }
  const openEditSchool = (s) => {
    setEditingSchool(s)
    setSchoolForm({ name: s.name || "", code: s.code || "" })
    setSchoolModalOpen(true)
  }
  const saveSchool = async () => {
    if (!schoolForm.name?.trim()) {
      setError("Name is required")
      return
    }
    setError("")
    try {
      if (editingSchool) await adminApi.schools.update(editingSchool._id, schoolForm)
      else await adminApi.schools.create(schoolForm)
      setSchoolModalOpen(false)
      load()
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to save school")
    }
  }
  const deleteSchool = async (id) => {
    if (!window.confirm("Remove this school?")) return
    try {
      await adminApi.schools.remove(id)
      load()
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to remove")
    }
  }

  const openCreateCourse = () => {
    setEditingCourse(null)
    setError("")
    setCourseForm({ name: "", code: "", templatePrompt: "", promptTemplates: [{ ...EMPTY_COURSE_TEMPLATE }] })
    setCourseModalOpen(true)
  }
  const openEditCourse = (c) => {
    setEditingCourse(c)
    const existingTemplates = Array.isArray(c.promptTemplates) && c.promptTemplates.length > 0
      ? c.promptTemplates.map((tpl) => ({
        name: tpl?.name || "",
        appliesTo: tpl?.appliesTo || "ANY",
        prompt: tpl?.prompt || ""
      }))
      : c.templatePrompt
        ? [{ name: "Topic idea generation", appliesTo: "TOPIC_IDEA", prompt: c.templatePrompt }]
        : [{ ...EMPTY_COURSE_TEMPLATE }]
    setCourseForm({ name: c.name || "", code: c.code || "", templatePrompt: c.templatePrompt || "", promptTemplates: existingTemplates })
    setCourseModalOpen(true)
  }
  const updateCourseTemplate = (idx, field, value) => {
    setCourseForm(prev => ({
      ...prev,
      promptTemplates: prev.promptTemplates.map((tpl, i) => (i === idx ? { ...tpl, [field]: value } : tpl))
    }))
  }
  const addCourseTemplate = () => {
    setCourseForm(prev => ({
      ...prev,
      promptTemplates: [...(prev.promptTemplates || []), { ...EMPTY_COURSE_TEMPLATE }]
    }))
  }
  const removeCourseTemplate = (idx) => {
    setCourseForm(prev => {
      const next = (prev.promptTemplates || []).filter((_, i) => i !== idx)
      return { ...prev, promptTemplates: next.length ? next : [{ ...EMPTY_COURSE_TEMPLATE }] }
    })
  }
  const saveCourse = async () => {
    if (!courseForm.name?.trim()) {
      setError("Name is required")
      return
    }
    setError("")
    try {
      const promptTemplates = (courseForm.promptTemplates || [])
        .map((tpl) => ({
          name: String(tpl?.name || "").trim(),
          appliesTo: String(tpl?.appliesTo || "ANY").trim() || "ANY",
          prompt: String(tpl?.prompt || "").trim()
        }))
        .filter((tpl) => tpl.name && tpl.prompt)
      const payload = {
        name: courseForm.name,
        code: courseForm.code,
        templatePrompt: String(courseForm.templatePrompt || "").trim(),
        promptTemplates
      }
      if (editingCourse) await adminApi.courses.update(editingCourse._id, payload)
      else await adminApi.courses.create(payload)
      setCourseModalOpen(false)
      load()
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to save course")
    }
  }
  const deleteCourse = async (id) => {
    if (!window.confirm("Remove this course?")) return
    try {
      await adminApi.courses.remove(id)
      load()
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to remove")
    }
  }

  const seedMissingCourseTemplates = async () => {
    if (!window.confirm("Seed missing default templates for all courses now?")) return
    setError("")
    setCourseSeeding(true)
    try {
      const res = await adminApi.courses.seedMissingTemplates()
      const data = res?.data?.data || {}
      await load()
      window.alert(`Templates seeded. Updated: ${data.updatedCourses || 0}, Unchanged: ${data.unchangedCourses || 0}`)
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to seed missing templates")
    } finally {
      setCourseSeeding(false)
    }
  }

  const seedNigerianSchools = async () => {
    if (!window.confirm("Seed all Nigerian tertiary institutions (universities, polytechnics, colleges)?")) return
    setError("")
    setSchoolSeeding(true)
    try {
      const res = await adminApi.schools.seedNigeria()
      const data = res?.data?.data || {}
      await Promise.all([load(), loadSummary()])
      window.alert(`Schools seeded. Created: ${data.created || 0}, Skipped: ${data.skipped || 0}, Total in list: ${data.total || 0}`)
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to seed Nigerian schools")
    } finally {
      setSchoolSeeding(false)
    }
  }

  const openCourseDetails = async (id) => {
    setError("")
    setCourseDetailsModalOpen(true)
    setCourseDetailsLoading(true)
    setCourseDetails(null)
    try {
      const res = await adminApi.courses.get(id)
      setCourseDetails(res?.data?.data || null)
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load course details")
    } finally {
      setCourseDetailsLoading(false)
    }
  }

  const handleLogout = () => {
    navigate("/admin", { replace: true })
  }

  const navItems = [
    { key: "schools", label: "Schools", icon: "M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75Z" },
    { key: "courses", label: "Courses", icon: "M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.905 59.905 0 0 1 12 3.493a59.902 59.902 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5" },
    { key: "ideas", label: "Ideas", icon: "M9 18h6m-7.5 0h.01M7.5 6a3 3 0 0 1 6 0v2a2 2 0 0 0 .586 1.414L16 12.414V15a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2v-2.586l1.414-1A2 2 0 0 0 10 8V6a3 3 0 0 1-2.5 0Z" },
    { key: "documentation", label: "Documentation", icon: "M7.5 3.75h9A2.25 2.25 0 0 1 18.75 6v12A2.25 2.25 0 0 1 16.5 20.25h-9A2.25 2.25 0 0 1 5.25 18V6A2.25 2.25 0 0 1 7.5 3.75Zm3 4.5h6M10.5 12h6M10.5 15.75h6" },
    { key: "payments", label: "Payments", icon: "M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15.5" },
    { key: "referrals", label: "Referrals", icon: "M11.25 9.75h1.5m-1.5 3h1.5m-7.5 6.75h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75h-12A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 19.5Z" },
    { key: "students", label: "Students", icon: "M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-3.382c0-1.023-.693-1.918-1.688-2.114A48.384 48.384 0 0 0 12 13.128v-3.382m0 0c1.02 0 2.002.115 2.688.263m-5.376 0a48.44 48.44 0 0 1 5.376 0" },
  ]

  const markReferralPaid = async (id) => {
    if (!window.confirm("Mark this referral as paid?")) return
    setError("")
    try {
      await adminApi.referrals.markPaid(id, {})
      await load()
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to mark as paid")
    }
  }

  const openStudentDetails = async (id) => {
    setError("")
    setStudentModalOpen(true)
    setStudentDetailsLoading(true)
    setStudentDetails(null)
    try {
      const res = await adminApi.students.get(id)
      setStudentDetails(res?.data?.data || null)
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load student details")
    } finally {
      setStudentDetailsLoading(false)
    }
  }

  const toggleStudentStatus = async () => {
    if (!studentDetails?._id) return
    const nextStatus = studentDetails.status === 1 ? 0 : 1
    const actionLabel = nextStatus === 1 ? "activate" : "deactivate"
    if (!window.confirm(`Are you sure you want to ${actionLabel} this student account?`)) return

    setStudentStatusSaving(true)
    setError("")
    try {
      await adminApi.students.setStatus(studentDetails._id, { status: nextStatus })
      setStudents(prev => prev.map(s => String(s._id) === String(studentDetails._id) ? { ...s, status: nextStatus } : s))
      await openStudentDetails(studentDetails._id)
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to update student status")
    } finally {
      setStudentStatusSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-base-200 text-base-content">
      {/* Top nav - matches student dashboard */}
      <nav className="fixed top-0 z-50 w-full bg-base-100 border-b border-base-300">
        <div className="px-3 py-3 lg:px-5 lg:pl-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-start">
              <div className="flex ms-2 md:me-24 items-center gap-2">
                <span className="self-center text-xl font-semibold sm:text-2xl whitespace-nowrap">AStudent</span>
                <span className="badge badge-sm">Admin</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="btn btn-sm btn-ghost" onClick={() => {
                if (showSummaryAnalytics) loadSummary()
                if (tab === "overview" || tab === "ideas" || tab === "documentation") return
                load()
              }} type="button">
                Refresh
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar - matches student dashboard */}
      <aside id="logo-sidebar" className="fixed top-0 left-0 z-40 w-64 h-screen pt-20 transition-transform -translate-x-full bg-base-100 border-r border-base-300 sm:translate-x-0" aria-label="Sidebar">
        <div className="h-full px-3 pb-4 overflow-y-auto bg-base-100">
          <ul className="space-y-4 font-medium pt-4">
            <li className={`py-1 rounded-xl ${showSummaryAnalytics ? "bg-primary/10 text-primary" : ""}`}>
              <button type="button" onClick={() => navigate("/admin/dashboard")} className="flex items-center w-full p-2 rounded-xl hover:bg-base-200 group cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75h6v6h-6v-6Zm10.5 0h6v10.5h-6V3.75ZM3.75 14.25h6v6h-6v-6Zm10.5 4.5h6v1.5h-6v-1.5Z" />
                </svg>
                <span className="ms-3 whitespace-nowrap">Dashboard</span>
              </button>
            </li>
            <li className="py-1 rounded-xl">
              <button type="button" onClick={() => navigate("/admin/reports")} className="flex items-center w-full p-2 rounded-xl hover:bg-base-200 group cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5h15M6.75 17.25V9.75M12 17.25V6.75M17.25 17.25v-12" />
                </svg>
                <span className="ms-3 whitespace-nowrap">Report</span>
              </button>
            </li>
            {navItems.map(({ key, label, icon }) => (
              <li key={key} className={`py-1 rounded-xl ${tab === key ? "bg-primary/10 text-primary" : ""}`}>
                <button
                  type="button"
                  onClick={() => navigate(key === "schools" ? "/admin/schools" : `/admin/dashboard?tab=${key}`)}
                  className="flex items-center w-full p-2 rounded-xl hover:bg-base-200 group cursor-pointer"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6 shrink-0">
                    <path strokeLinecap="round" strokeLinejoin="round" d={icon} />
                  </svg>
                  <span className="ms-3 whitespace-nowrap">{label}</span>
                </button>
              </li>
            ))}
            <li className="pt-4 mt-4 border-t border-base-300">
              <button type="button" onClick={handleLogout} className="flex items-center w-full p-2 rounded-xl hover:bg-base-200 group">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-6">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15m-3 0-3-3m0 0 3-3m-3 3H15" />
                </svg>
                <span className="flex-1 ms-3 whitespace-nowrap text-left">Logout</span>
              </button>
            </li>
          </ul>
        </div>
      </aside>

      {/* Main content - matches student dashboard */}
      <div className="p-4 sm:ml-64">
        <div className="p-5 border border-base-300 rounded-2xl mt-14 bg-base-100 min-h-[calc(100vh-8rem)] shadow-soft">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-2xl font-bold">{TABS[tab] || "Dashboard"}</h1>
              <p className="text-sm text-base-content/60 mt-1">
                {showSummaryAnalytics
                  ? "Quick admin summary analytics."
                  : tab === "ideas"
                  ? "Search and filter saved ideas submitted by students."
                  : tab === "documentation"
                    ? "Browse generated documentation sets created by students."
                    : "Manage core data, payments, and referrals."}
              </p>
            </div>
          </div>
          {showSummaryAnalytics ? (
            <div className="mb-5 rounded-2xl border border-base-300 bg-base-100 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">Summary analytics</h2>
                {summaryLoading ? <span className="text-xs text-base-content/60">Loading...</span> : null}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                <div className="rounded-xl border border-base-300 p-3">
                  <p className="text-xs uppercase text-base-content/60">Students</p>
                  <p className="text-xl font-bold">{summary.students}</p>
                </div>
                <div className="rounded-xl border border-base-300 p-3">
                  <p className="text-xs uppercase text-base-content/60">Schools</p>
                  <p className="text-xl font-bold">{summary.schools}</p>
                </div>
                <div className="rounded-xl border border-base-300 p-3">
                  <p className="text-xs uppercase text-base-content/60">Courses</p>
                  <p className="text-xl font-bold">{summary.courses}</p>
                </div>
                <div className="rounded-xl border border-base-300 p-3">
                  <p className="text-xs uppercase text-base-content/60">Ideas</p>
                  <p className="text-xl font-bold">{summary.ideas}</p>
                </div>
                <div className="rounded-xl border border-base-300 p-3">
                  <p className="text-xs uppercase text-base-content/60">Documentation</p>
                  <p className="text-xl font-bold">{summary.docsDocumentation}</p>
                </div>
                <div className="rounded-xl border border-base-300 p-3">
                  <p className="text-xs uppercase text-base-content/60">Proposal</p>
                  <p className="text-xl font-bold">{summary.docsProposal}</p>
                </div>
                <div className="rounded-xl border border-base-300 p-3">
                  <p className="text-xs uppercase text-base-content/60">SIWES Report</p>
                  <p className="text-xl font-bold">{summary.docsSiwes}</p>
                </div>
                <div className="rounded-xl border border-base-300 p-3">
                  <p className="text-xs uppercase text-base-content/60">Referrals</p>
                  <p className="text-xl font-bold">{summary.referrals}</p>
                </div>
              </div>
            </div>
          ) : null}
          {error && <p className="text-error mb-2">{error}</p>}
          {showSummaryAnalytics ? (
            <div className="text-sm text-base-content/70">
              Select a section from the sidebar to manage schools, courses, formats, ideas, documentation, payments, referrals, and students.
            </div>
          ) : tab === "ideas" ? (
            <AdminIdeas students={filterStudents} />
          ) : tab === "documentation" ? (
            <AdminDocumentations students={filterStudents} />
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <span className="loading loading-spinner loading-lg text-primary"></span>
            </div>
          ) : tab === "schools" ? (
          <div>
            <div className="flex justify-end gap-2 mb-4">
              <button className={`btn btn-outline btn-sm ${schoolSeeding ? "btn-disabled" : ""}`} onClick={seedNigerianSchools}>
                {schoolSeeding ? "Seeding..." : "Seed Nigerian institutions"}
              </button>
              <button className="btn btn-primary btn-sm" onClick={openCreateSchool}>Add school</button>
            </div>
            <div className="overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr><th>Name</th><th>Code</th><th></th></tr>
                </thead>
                <tbody>
                  {schools.map(s => (
                    <tr key={s._id}>
                      <td>{s.name}</td>
                      <td>{s.code || "—"}</td>
                      <td>
                        <button className="btn btn-ghost btn-xs mr-1" onClick={() => openEditSchool(s)}>Edit</button>
                        <button className="btn btn-ghost btn-xs text-error" onClick={() => deleteSchool(s._id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <dialog open={schoolModalOpen} className="modal modal-bottom sm:modal-middle">
              <div className="modal-box bg-base-100 text-base-content">
                <h2 className="text-xl font-bold">{editingSchool ? "Edit school" : "Add school"}</h2>
                <div className="form-control mt-4">
                  <label className="label">Name</label>
                  <input type="text" className="input input-bordered" value={schoolForm.name} onChange={e => setSchoolForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. University of Lagos" />
                </div>
                <div className="form-control mt-4">
                  <label className="label">Code (optional)</label>
                  <input type="text" className="input input-bordered" value={schoolForm.code} onChange={e => setSchoolForm(prev => ({ ...prev, code: e.target.value }))} placeholder="e.g. unilag" disabled={!!editingSchool} />
                </div>
                <div className="modal-action">
                  <button type="button" className="btn" onClick={() => setSchoolModalOpen(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={saveSchool}>Save</button>
                </div>
              </div>
              <form method="dialog" className="modal-backdrop" onClick={() => setSchoolModalOpen(false)}><button>close</button></form>
            </dialog>
          </div>
        ) : tab === "courses" ? (
          <div>
            <div className="flex justify-end gap-2 mb-4">
              <button className={`btn btn-outline btn-sm ${courseSeeding ? "btn-disabled" : ""}`} onClick={seedMissingCourseTemplates}>
                {courseSeeding ? "Seeding..." : "Seed missing templates"}
              </button>
              <button className="btn btn-primary btn-sm" onClick={openCreateCourse}>Add course</button>
            </div>
            <div className="overflow-x-auto">
              <table className="table table-zebra">
                <thead>
                  <tr><th>Name</th><th>Code</th><th>Course templates</th><th></th></tr>
                </thead>
                <tbody>
                  {courses.map(c => (
                    <tr key={c._id}>
                      <td>{c.name}</td>
                      <td>{c.code || "—"}</td>
                      <td>
                        {Array.isArray(c.promptTemplates) && c.promptTemplates.length > 0
                          ? <span className="badge badge-success badge-sm">{c.promptTemplates.length} templates</span>
                          : c.templatePrompt?.trim()
                            ? <span className="badge badge-warning badge-sm">Legacy single template</span>
                            : <span className="badge badge-ghost badge-sm">None</span>}
                      </td>
                      <td>
                        <button className="btn btn-ghost btn-xs mr-1" onClick={() => openCourseDetails(c._id)}>View</button>
                        <button className="btn btn-ghost btn-xs mr-1" onClick={() => openEditCourse(c)}>Edit</button>
                        <button className="btn btn-ghost btn-xs text-error" onClick={() => deleteCourse(c._id)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <dialog open={courseModalOpen} className="modal modal-bottom sm:modal-middle">
              <div className="modal-box bg-base-100 text-base-content">
                <h2 className="text-xl font-bold">{editingCourse ? "Edit course" : "Add course"}</h2>
                <div className="form-control mt-4">
                  <label className="label">Name</label>
                  <input type="text" className="input input-bordered" value={courseForm.name} onChange={e => setCourseForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Computer Science" />
                </div>
                <div className="form-control mt-4">
                  <label className="label">Code (optional)</label>
                  <input type="text" className="input input-bordered" value={courseForm.code} onChange={e => setCourseForm(prev => ({ ...prev, code: e.target.value }))} placeholder="e.g. csc" />
                </div>
                <div className="form-control mt-4">
                  <label className="label">Legacy single template prompt (optional fallback)</label>
                  <textarea
                    className="textarea textarea-bordered w-full min-h-[120px]"
                    value={courseForm.templatePrompt}
                    onChange={e => setCourseForm(prev => ({ ...prev, templatePrompt: e.target.value }))}
                    placeholder={"Optional fallback template.\nYou can use: {{school}}, {{course}}, {{projectType}}, {{preference}}"}
                  />
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <label className="label">Course template prompts</label>
                    <button type="button" className="btn btn-ghost btn-xs" onClick={addCourseTemplate}>+ Add template</button>
                  </div>
                  <div className="space-y-3">
                    {(courseForm.promptTemplates || []).map((tpl, idx) => (
                      <div key={idx} className="rounded-xl border border-base-300 p-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <input
                            type="text"
                            className="input input-bordered w-full"
                            value={tpl.name}
                            onChange={(e) => updateCourseTemplate(idx, "name", e.target.value)}
                            placeholder="Template name (e.g. SIWES report)"
                          />
                          <input
                            type="text"
                            className="input input-bordered w-full"
                            value={tpl.appliesTo}
                            onChange={(e) => updateCourseTemplate(idx, "appliesTo", e.target.value)}
                            placeholder="Applies to (e.g. SIWES_REPORT, TOPIC_IDEA, ANY)"
                          />
                        </div>
                        <textarea
                          className="textarea textarea-bordered w-full min-h-[120px] mt-2"
                          value={tpl.prompt}
                          onChange={(e) => updateCourseTemplate(idx, "prompt", e.target.value)}
                          placeholder={"Write template prompt.\nVars: {{school}}, {{course}}, {{projectType}}, {{preference}}"}
                        />
                        <div className="flex justify-end mt-2">
                          <button type="button" className="btn btn-ghost btn-xs text-error" onClick={() => removeCourseTemplate(idx)}>Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="modal-action">
                  <button type="button" className="btn" onClick={() => setCourseModalOpen(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary" onClick={saveCourse}>Save</button>
                </div>
              </div>
              <form method="dialog" className="modal-backdrop" onClick={() => setCourseModalOpen(false)}><button>close</button></form>
            </dialog>
          </div>
        ) : tab === "payments" ? (
          <div className="overflow-x-auto">
            <div className="mb-3 text-sm text-slate-600">
              Showing only successful payments.
            </div>
            <table className="table table-zebra">
              <thead>
                <tr><th>Date</th><th>Student</th><th>Amount</th><th>Type</th><th>Status</th></tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p._id}>
                    <td>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "—"}</td>
                    <td>{p.student?.email || p.student?.name || p.student?._id}</td>
                    <td>₦{p.amount}</td>
                    <td>{p.productType}</td>
                    <td>{p.paymentStatus}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === "referrals" ? (
          <div className="overflow-x-auto">
            <div className="mb-3 text-sm text-slate-600">
              Showing unpaid referral commissions (₦2,000 each).
            </div>
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th>Activated</th>
                  <th>Referred student</th>
                  <th>Referrer</th>
                  <th>Bank details</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {referrals.map(r => (
                  <tr key={r._id}>
                    <td>{r.activationPayment?.createdAt ? new Date(r.activationPayment.createdAt).toLocaleDateString() : (r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "—")}</td>
                    <td>{r.referredStudent?.email || r.referredStudent?.name || "—"}</td>
                    <td>{r.referrer?.email || r.referrer?.name || "—"}</td>
                    <td className="text-xs">
                      <div>{r.referrer?.bankDetails?.bankName || "—"}</div>
                      <div>{r.referrer?.bankDetails?.accountNumber || "—"}</div>
                      <div>{r.referrer?.bankDetails?.accountName || "—"}</div>
                    </td>
                    <td>₦{r.amount ?? 2000}</td>
                    <td>{r.status}</td>
                    <td>
                      {r.status !== "paid" ? (
                        <button className="btn btn-primary btn-xs" onClick={() => markReferralPaid(r._id)}>
                          Mark paid
                        </button>
                      ) : (
                        <span className="badge badge-success badge-sm">Paid</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>School</th><th>Level</th>
                  <th>Projects</th><th>Docs</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {students.map(s => (
                  <tr key={s._id}>
                    <td>{s.name}</td>
                    <td>{s.email}</td>
                    <td>{s.school}</td>
                    <td>{s.level}</td>
                    <td>{s.projectCount ?? "—"}</td>
                    <td>{s.docCount ?? "—"}</td>
                    <td>
                      {s.status === 1 ? (
                        <span className="badge badge-success badge-sm">Active</span>
                      ) : (
                        <span className="badge badge-error badge-sm">Inactive</span>
                      )}
                    </td>
                    <td>
                      <button className="btn btn-ghost btn-xs" onClick={() => openStudentDetails(s._id)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <dialog open={courseDetailsModalOpen} className="modal modal-bottom sm:modal-middle">
          <div className="modal-box max-w-3xl bg-base-100 text-base-content">
            <h2 className="text-xl font-bold">Course Details</h2>

            {courseDetailsLoading ? (
              <div className="py-6">
                <span className="loading loading-spinner loading-lg text-primary"></span>
              </div>
            ) : courseDetails ? (
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p><span className="font-semibold">Name:</span> {courseDetails.name || "—"}</p>
                    <p><span className="font-semibold">Code:</span> {courseDetails.code || "—"}</p>
                    <p><span className="font-semibold">Status:</span> {courseDetails.activeStatus === 1 ? <span className="badge badge-success badge-sm">Active</span> : <span className="badge badge-error badge-sm">Inactive</span>}</p>
                  </div>
                  <div className="space-y-2">
                    <p><span className="font-semibold">Templates:</span> {Array.isArray(courseDetails.promptTemplates) ? courseDetails.promptTemplates.length : 0}</p>
                    <p><span className="font-semibold">Created:</span> {courseDetails.createdAt ? new Date(courseDetails.createdAt).toLocaleDateString() : "—"}</p>
                    <p><span className="font-semibold">Updated:</span> {courseDetails.updatedAt ? new Date(courseDetails.updatedAt).toLocaleDateString() : "—"}</p>
                  </div>
                </div>

                {courseDetails.templatePrompt?.trim() ? (
                  <details className="rounded-xl border border-base-300 p-3">
                    <summary className="cursor-pointer font-semibold">Legacy single template prompt</summary>
                    <pre className="mt-2 whitespace-pre-wrap text-sm bg-base-200/40 p-3 rounded-lg overflow-auto">{courseDetails.templatePrompt}</pre>
                  </details>
                ) : null}

                <div className="rounded-xl border border-base-300 p-3">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold">Course template prompts</p>
                    {Array.isArray(courseDetails.promptTemplates) && courseDetails.promptTemplates.length > 0 ? (
                      <span className="badge badge-neutral badge-sm">{courseDetails.promptTemplates.length}</span>
                    ) : (
                      <span className="badge badge-ghost badge-sm">None</span>
                    )}
                  </div>

                  {Array.isArray(courseDetails.promptTemplates) && courseDetails.promptTemplates.length > 0 ? (
                    <div className="mt-3 space-y-3">
                      {courseDetails.promptTemplates.map((tpl) => (
                        <details key={tpl?._id || `${tpl?.name}-${tpl?.appliesTo}`} className="rounded-xl border border-base-300 p-3">
                          <summary className="cursor-pointer">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-semibold">{tpl?.name || "Untitled template"}</span>
                              <span className="badge badge-outline badge-sm">{tpl?.appliesTo || "ANY"}</span>
                              {tpl?.parseStatus ? (
                                <span className={`badge badge-sm ${tpl.parseStatus === "PARSED" ? "badge-success" : tpl.parseStatus === "FAILED" ? "badge-error" : "badge-warning"}`}>
                                  {tpl.parseStatus}
                                </span>
                              ) : null}
                            </div>
                          </summary>
                          {tpl?.parseError ? (
                            <p className="mt-2 text-xs text-error">Parse error: {tpl.parseError}</p>
                          ) : null}
                          {tpl?.prompt ? (
                            <pre className="mt-2 whitespace-pre-wrap text-sm bg-base-200/40 p-3 rounded-lg overflow-auto">{tpl.prompt}</pre>
                          ) : (
                            <p className="mt-2 text-sm text-base-content/70">No prompt.</p>
                          )}
                        </details>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-base-content/70">No templates added for this course yet.</p>
                  )}
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-base-content/70">No course loaded.</p>
            )}

            {error && <p className="text-error text-sm mt-3">{error}</p>}

            <div className="modal-action">
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setCourseDetailsModalOpen(false)
                  setCourseDetails(null)
                }}
              >
                Close
              </button>
              {courseDetails?._id ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setCourseDetailsModalOpen(false)
                    setCourseDetails(null)
                    openEditCourse(courseDetails)
                  }}
                  disabled={courseDetailsLoading}
                >
                  Edit course
                </button>
              ) : null}
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={() => setCourseDetailsModalOpen(false)}>
            <button>close</button>
          </form>
        </dialog>

        <dialog open={studentModalOpen} className="modal modal-bottom sm:modal-middle">
          <div className="modal-box max-w-3xl bg-base-100 text-base-content">
            <h2 className="text-xl font-bold">Student Details</h2>

            {studentDetailsLoading ? (
              <div className="py-6">
                <span className="loading loading-spinner loading-lg text-primary"></span>
              </div>
            ) : studentDetails ? (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p><span className="font-semibold">Name:</span> {studentDetails.name}</p>
                  <p><span className="font-semibold">Email:</span> {studentDetails.email}</p>
                  <p><span className="font-semibold">Phone:</span> {studentDetails.phoneNumber || "—"}</p>
                  <p><span className="font-semibold">School:</span> {studentDetails.school || "—"}</p>
                  <p><span className="font-semibold">Department:</span> {studentDetails.department || "—"}</p>
                  <p><span className="font-semibold">Level:</span> {studentDetails.level || "—"}</p>
                </div>
                <div className="space-y-2">
                  <p><span className="font-semibold">Matric:</span> {studentDetails.matric || "—"}</p>
                  <p><span className="font-semibold">Country:</span> {studentDetails.country || "—"}</p>
                  <p><span className="font-semibold">State:</span> {studentDetails.state || "—"}</p>
                  <p><span className="font-semibold">Onboarding:</span> {studentDetails.onboardingStatus || "—"}</p>
                  <p><span className="font-semibold">Role:</span> {studentDetails.role || "—"}</p>
                  <p>
                    <span className="font-semibold">Account Status:</span>{" "}
                    {studentDetails.status === 1 ? (
                      <span className="badge badge-success badge-sm">Active</span>
                    ) : (
                      <span className="badge badge-error badge-sm">Inactive</span>
                    )}
                  </p>
                </div>

                <div className="space-y-2 sm:col-span-2 border-t border-base-300 pt-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <p><span className="font-semibold">Referral Code:</span> {studentDetails.referralCode || "—"}</p>
                      <p><span className="font-semibold">Referred By Code:</span> {studentDetails.referredByCode || "—"}</p>
                      <p><span className="font-semibold">Projects (active):</span> {studentDetails.projectCount ?? "—"}</p>
                      <p><span className="font-semibold">Docs (active):</span> {studentDetails.docCount ?? "—"}</p>
                    </div>
                    <div className="space-y-2">
                      <p><span className="font-semibold">Bank Name:</span> {studentDetails.bankDetails?.bankName || "—"}</p>
                      <p><span className="font-semibold">Account Name:</span> {studentDetails.bankDetails?.accountName || "—"}</p>
                      <p><span className="font-semibold">Account Number:</span> {studentDetails.bankDetails?.accountNumber || "—"}</p>
                      <p><span className="font-semibold">Created:</span> {studentDetails.createdAt ? new Date(studentDetails.createdAt).toLocaleDateString() : "—"}</p>
                    </div>
                  </div>

                  {studentDetails.usage ? (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="rounded-xl border border-base-300 p-3">
                        <p className="font-semibold">Idea generation usage</p>
                        <p className="text-sm mt-1">
                          Used: <span className="font-semibold">{studentDetails.usage.ideaGeneration?.used ?? 0}</span>{" "}
                          / Allowed: <span className="font-semibold">{studentDetails.usage.ideaGeneration?.allowed ?? 0}</span>{" "}
                          (Remaining: <span className="font-semibold">{studentDetails.usage.ideaGeneration?.remaining ?? 0}</span>)
                        </p>
                      </div>
                      <div className="rounded-xl border border-base-300 p-3">
                        <p className="font-semibold">Project generation (documentation) usage</p>
                        <p className="text-sm mt-1">
                          Doc sets: <span className="font-semibold">{studentDetails.usage.documentation?.totalDocSets ?? 0}</span>
                        </p>
                        <p className="text-sm mt-1">
                          Edits used: <span className="font-semibold">{studentDetails.usage.documentation?.totals?.editsUsed ?? 0}</span>{" "}
                          (Remaining: <span className="font-semibold">{studentDetails.usage.documentation?.totals?.editsRemaining ?? 0}</span>)
                        </p>
                        <p className="text-sm mt-1">
                          Regenerations used: <span className="font-semibold">{studentDetails.usage.documentation?.totals?.regenerationsUsed ?? 0}</span>{" "}
                          (Remaining: <span className="font-semibold">{studentDetails.usage.documentation?.totals?.regenerationsRemaining ?? 0}</span>)
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-base-content/70">No student loaded.</p>
            )}

            {error && <p className="text-error text-sm mt-3">{error}</p>}

            <div className="modal-action">
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setStudentModalOpen(false)
                  setStudentDetails(null)
                }}
                disabled={studentStatusSaving}
              >
                Close
              </button>
              <button
                type="button"
                className={`btn ${studentDetails?.status === 1 ? "btn-warning" : "btn-success"}`}
                onClick={toggleStudentStatus}
                disabled={!studentDetails || studentStatusSaving || studentDetailsLoading}
              >
                {studentDetails?.status === 1 ? "Deactivate account" : "Activate account"}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={() => setStudentModalOpen(false)}>
            <button>close</button>
          </form>
        </dialog>

        </div>
      </div>
    </div>
  )
}
